import { useState, useMemo } from "react";
import { useRiskData } from "./hooks/useRiskData";
import { Header } from "./components/Header";
import { SummaryCards } from "./components/SummaryCards";
import { HeatmapSection } from "./components/HeatmapSection";
import { HealthFactorChart } from "./components/HealthFactorChart";
import { ConcentrationTable } from "./components/ConcentrationTable";
import { UtilizationSection } from "./components/UtilizationSection";
import { LiquiditySection } from "./components/LiquiditySection";
import { SlippageCurveChart } from "./components/SlippageCurveChart";
import { HistoryChart } from "./components/HistoryChart";
import { useHistoryData } from "./hooks/useHistoryData";
import { MethodologyPage } from "./components/MethodologyPage";
import type { ProtocolFilter, MetricsBlock } from "./types";

type Page = "dashboard" | "methodology";

export default function App() {
  const { data, status, error, lastFetched } = useRiskData();
  const { history } = useHistoryData();
  const [protocolFilter, setProtocolFilter] = useState<ProtocolFilter>("all");
  const [page, setPage] = useState<Page>("dashboard");

  // Resolve active metrics based on protocol filter
  const activeMetrics: MetricsBlock | null = useMemo(() => {
    if (!data) return null;
    if (protocolFilter === "all") return data.metrics;
    return data.metrics_by_protocol?.[protocolFilter] ?? data.metrics;
  }, [data, protocolFilter]);

  return (
    <div className="min-h-screen bg-surface-900">
      {/* ── Header ──────────────────────────────────────────── */}
      <Header
        status={status}
        lastFetched={lastFetched}
        dataTimestamp={data?.timestamp ?? null}
        protocolFilter={protocolFilter}
        onProtocolChange={setProtocolFilter}
        activeProtocols={data?.meta.protocols_active ?? []}
        page={page}
        onPageChange={setPage}
      />

      {/* ── Page content ─────────────────────────────────────── */}
      {page === "methodology" ? (
        <MethodologyPage onBack={() => setPage("dashboard")} />
      ) : (
        <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-3 sm:space-y-4">
          {/* Loading / error states */}
          {status === "loading" && !data && (
            <div className="card text-center py-20">
              <p className="text-text-secondary">Loading risk data...</p>
            </div>
          )}

          {status === "error" && !data && (
            <div className="card text-center py-20 border-risk-danger">
              <p className="text-risk-danger font-medium">Failed to load data</p>
              <p className="text-text-muted text-sm mt-2">{error}</p>
            </div>
          )}

          {/* Stale data warning — subtle inline */}
          {status === "stale" && data && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-risk-warning/8 border border-risk-warning/20 text-xs">
              <span className="text-risk-warning">⚠</span>
              <span className="text-text-secondary">
                Data from {new Date(data.timestamp).toLocaleTimeString()} — metrics may lag current market
              </span>
            </div>
          )}

          {/* Dashboard content (renders even with stale data) */}
          {data && activeMetrics && (
            <>
              {/* Summary cards row */}
              <SummaryCards
                metrics={activeMetrics}
                protocolFilter={protocolFilter}
              />

              {/* Liquidity-at-Risk */}
            {data.metrics.liquidity_at_risk && (
              <LiquiditySection
                cascade={data.metrics.cascade}
                lar={data.metrics.liquidity_at_risk}
                liquidity={data.liquidity}
                protocolFilter={protocolFilter}
              />
            )}

              {/* Liquidation exposure heatmap */}
              <HeatmapSection
                car={activeMetrics.collateral_at_risk}
                protocolFilter={protocolFilter}
              />

            {/* DEX liquidity depth curves */}
            {data.liquidity && Object.keys(data.liquidity.curves ?? {}).length > 0 && (
              <SlippageCurveChart liquidity={data.liquidity} lar={data.metrics.liquidity_at_risk} />
            )}

            {/* HF distribution + concentration */}
              <div className="grid grid-cols-1 gap-4">
                <HealthFactorChart
                  distribution={activeMetrics.health_factor_distribution}
                  protocolFilter={protocolFilter}
                />
                <ConcentrationTable
                  concentration={activeMetrics.concentration}
                  protocolFilter={protocolFilter}
                />
              </div>

              {/* Historical trends */}
            {history.length >= 2 && (
              <HistoryChart history={history} />
            )}

            {/* Utilization & interest rate curves */}
              {activeMetrics.utilization && (
                <UtilizationSection
                  utilization={activeMetrics.utilization}
                  protocolFilter={protocolFilter}
                />
              )}
            </>
          )}
        </main>
      )}

      {/* ── Footer ──────────────────────────────────────────── */}
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 py-8 border-t border-surface-600">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-text-muted text-xs">
          <div className="flex items-center gap-3">
            <a href="https://sepperlabs.com" className="hover:text-text-primary transition-colors">
              SepperLabs
            </a>
            <span>·</span>
            <a href="https://github.com/osepper/defi-risk-dashboard" className="hover:text-text-primary transition-colors">
              GitHub
            </a>
            <span>·</span>
            <button
              onClick={() => { setPage("methodology"); window.scrollTo(0, 0); }}
              className="hover:text-text-primary transition-colors"
            >
              Methodology
            </button>
            <span>·</span>
            <a href="mailto:feedback@sepperlabs.com" className="hover:text-text-primary transition-colors">
              Feedback
            </a>
          </div>
          <p>
            Data: The Graph, DeFiLlama, Chainlink. Updated every ~15 min. Not financial advice.
          </p>
        </div>
      </footer>
    </div>
  );
}
